// ============================================================================
// AR OUTSTANDING REPORT
//
// The manager's day-to-day "what does each customer owe us as at a date" view.
// Balances come from the ACCOUNTING RECORDS (net GL movement on the receivables
// ledgers), so the total ties to the AR control account. That is deliberately
// different from the Debtors Ageing report, which reads open items/bills — this
// one also carries unallocated receipts, advances and direct journals.
//
// Drill-down: Customer Group -> Customer ledgers -> Ledger Statement (+ its
// transaction lines). Opening a transaction in edit mode needs the app-level
// cross-screen navigation seam (LayoutConfig.onNavigate), which is not wired
// up yet — see the user guide's "Known limitation".
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutSelect, type SelectOption, SyutGrid, type SyutGridColumn } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { reportService, customerGroupService } from 'api/services';
import type {
  ArOutstandingResultDto, ArOutstandingRowDto, ArOutstandingQueryParams,
  LedgerStatementDto, PartyGroupDto,
} from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';
import ReportHeader from 'components/reports/ReportHeader';
import DrillableCell from 'components/reports/DrillableCell';

type ViewMode = 'GROUPED' | 'DETAILED';

/** SyutGrid requires every row to carry an `id`; the report has no natural single key. */
type ArRow = ArOutstandingRowDto & { id: string };

const today = () => new Date().toISOString().slice(0, 10);

const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A group we've drilled into. `id === null` means the synthetic Ungrouped bucket. */
interface DrillTarget { id: string | null; name: string; }

interface ArOutstandingScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ArOutstandingScreen: React.FC<ArOutstandingScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('ar-outstanding');
  const { accessibleEntities, accessibleBusinessUnits } = useAuthContext();

  // --- filters ---
  const [asOnDate, setAsOnDate] = useState<string>(today());
  const [viewMode, setViewMode] = useState<ViewMode>('GROUPED');
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedBuIds, setSelectedBuIds] = useState<string[]>([]);
  const [groupFilterId, setGroupFilterId] = useState<string>('');
  const [includeZero, setIncludeZero] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // --- drill-down state ---
  const [drill, setDrill] = useState<DrillTarget | null>(null);
  const [statementLedger, setStatementLedger] = useState<ArOutstandingRowDto | null>(null);

  // --- data ---
  const [data, setData] = useState<ArOutstandingResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [groups, setGroups] = useState<PartyGroupDto[]>([]);

  // Applied filters — the report reflects ONLY what was applied (Apply button).
  // View mode, drill and pagination are immediate and re-query the applied set.
  const [applied, setApplied] = useState<{ asOnDate: string; entityIds: string[]; businessUnitIds: string[]; groupFilterId: string; includeZero: boolean }>(
    { asOnDate: today(), entityIds: [], businessUnitIds: [], groupFilterId: '', includeZero: false },
  );

  const entityOptions: SelectOption[] = useMemo(
    () => accessibleEntities.map(e => ({ value: e.id, label: `${e.entityCode} — ${e.entityName}` })),
    [accessibleEntities],
  );
  const buOptions: SelectOption[] = useMemo(() => {
    const scoped = selectedEntityIds.length > 0
      ? accessibleBusinessUnits.filter(b => selectedEntityIds.includes(b.entityId))
      : accessibleBusinessUnits;
    return scoped.map(b => ({ value: b.id, label: b.buName }));
  }, [accessibleBusinessUnits, selectedEntityIds]);

  // Drop BU picks that no longer belong to the selected entities.
  useEffect(() => {
    if (selectedEntityIds.length === 0 || selectedBuIds.length === 0) return;
    const allowed = new Set(buOptions.map(o => String(o.value)));
    const next = selectedBuIds.filter(id => allowed.has(id));
    if (next.length !== selectedBuIds.length) setSelectedBuIds(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityIds]);

  useEffect(() => {
    customerGroupService.getLookup().then(setGroups).catch(() => setGroups([]));
  }, []);

  const groupOptions: SelectOption[] = useMemo(
    () => [{ value: '', label: 'All groups' },
           ...groups.map(g => ({ value: g.id, label: `${g.groupCode} — ${g.groupName}` }))],
    [groups],
  );

  const buildParams = useCallback((): ArOutstandingQueryParams => ({
    asOnDate: applied.asOnDate,
    // A drill-down forces the detailed list for the group we clicked into.
    viewMode: drill ? 'DETAILED' : viewMode,
    partyGroupId: drill ? (drill.id ?? undefined) : (applied.groupFilterId || undefined),
    ungroupedOnly: drill ? drill.id === null : undefined,
    includeZeroBalance: applied.includeZero || undefined,
    entityIds: applied.entityIds.length > 0 ? applied.entityIds : undefined,
    businessUnitIds: applied.businessUnitIds.length > 0 ? applied.businessUnitIds : undefined,
    page,
    pageSize,
  }), [applied, viewMode, drill, page, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await reportService.getArOutstanding(buildParams()));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load AR Outstanding.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Fetch when the APPLIED filters (or immediate view/drill/page) change — never
  // while the user is still editing the filter panel.
  useEffect(() => { load(); }, [load]);

  // Commit draft filters -> applied; resets drill + page (a fresh filter).
  const applyFilters = useCallback(() => {
    if (!asOnDate) { notificationService.toast.warning('As On Date is required.'); return; }
    setApplied({ asOnDate, entityIds: selectedEntityIds, businessUnitIds: selectedBuIds, groupFilterId, includeZero });
    setDrill(null); setPage(1); setFilterOpen(false);
  }, [asOnDate, selectedEntityIds, selectedBuIds, groupFilterId, includeZero]);

  const isStale = useMemo(() =>
    JSON.stringify({ a: asOnDate, e: selectedEntityIds, b: selectedBuIds, g: groupFilterId, z: includeZero })
    !== JSON.stringify({ a: applied.asOnDate, e: applied.entityIds, b: applied.businessUnitIds, g: applied.groupFilterId, z: applied.includeZero }),
    [asOnDate, selectedEntityIds, selectedBuIds, groupFilterId, includeZero, applied]);

  const handleExport = useCallback(async () => {
    try {
      await reportService.exportArOutstanding(buildParams());
      notificationService.toast.success('Export downloaded.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not export.');
    }
  }, [buildParams]);

  // --- drill handlers ---
  const drillIntoGroup = useCallback((r: ArOutstandingRowDto) => {
    setDrill({ id: r.groupId ?? null, name: r.groupName });
    setPage(1);
  }, []);

  const clearDrill = useCallback(() => { setDrill(null); setPage(1); }, []);

  const effectiveMode: ViewMode = drill ? 'DETAILED' : viewMode;

  // SyutGrid keys rows by `id`. The report has no natural single id, so derive a stable
  // one: the ledger in detailed mode, the group (or the Ungrouped sentinel) when grouped.
  const gridRows: ArRow[] = useMemo(
    () => (data?.rows ?? []).map(r => ({
      ...r,
      id: r.ledgerId ?? r.groupId ?? 'ungrouped',
    })),
    [data],
  );

  // --- columns ---
  const groupedColumns: SyutGridColumn<ArRow>[] = useMemo(() => [
    {
      id: 'groupCode', header: 'Group Code', accessorKey: 'groupCode', size: 150,
      cell: ({ row }) => {
        const r = row.original as ArRow;
        return (
          <DrillableCell onClick={() => drillIntoGroup(r)} title="Show the customers in this group">
            {r.groupCode}
          </DrillableCell>
        );
      },
    },
    {
      id: 'groupName', header: 'Customer Group', accessorKey: 'groupName', size: 300,
      cell: ({ row }) => {
        const r = row.original as ArRow;
        return (
          <DrillableCell onClick={() => drillIntoGroup(r)} title="Show the customers in this group">
            {r.groupName}
          </DrillableCell>
        );
      },
    },
    {
      id: 'customerCount', header: 'Customers', accessorKey: 'customerCount', size: 110,
      cell: ({ row }) => (
        <span style={{ display: 'block', textAlign: 'right' }}>
          {(row.original as ArRow).customerCount}
        </span>
      ),
    },
    {
      id: 'outstanding', header: 'Outstanding', accessorKey: 'outstanding', size: 170,
      cell: ({ row }) => {
        const v = (row.original as ArRow).outstanding;
        return (
          <span style={{ display: 'block', textAlign: 'right', color: v < 0 ? 'var(--danger-color, #dc2626)' : undefined }}>
            {fmtMoney(v)}
          </span>
        );
      },
    },
  ], [drillIntoGroup]);

  const detailedColumns: SyutGridColumn<ArRow>[] = useMemo(() => [
    { id: 'groupName', header: 'Customer Group', accessorKey: 'groupName', size: 200 },
    { id: 'partyCode', header: 'Customer Code', accessorKey: 'partyCode', size: 130,
      cell: ({ row }) => (row.original as ArRow).partyCode || '—' },
    {
      id: 'partyName', header: 'Customer', accessorKey: 'partyName', size: 280,
      cell: ({ row }) => {
        const r = row.original as ArRow;
        return (
          <DrillableCell onClick={() => setStatementLedger(r)} title="Open the ledger statement">
            {r.partyName || r.ledgerName || '—'}
          </DrillableCell>
        );
      },
    },
    {
      id: 'ledgerCode', header: 'Ledger', accessorKey: 'ledgerCode', size: 140,
      cell: ({ row }) => {
        const r = row.original as ArRow;
        return (
          <DrillableCell onClick={() => setStatementLedger(r)} title="Open the ledger statement">
            {r.ledgerCode || '—'}
          </DrillableCell>
        );
      },
    },
    { id: 'entityCode', header: 'Entity', accessorKey: 'entityCode', size: 110,
      cell: ({ row }) => (row.original as ArRow).entityCode || '—' },
    {
      id: 'outstanding', header: 'Outstanding', accessorKey: 'outstanding', size: 170,
      cell: ({ row }) => {
        const v = (row.original as ArRow).outstanding;
        return (
          <span style={{ display: 'block', textAlign: 'right', color: v < 0 ? 'var(--danger-color, #dc2626)' : undefined }}>
            {fmtMoney(v)}
          </span>
        );
      },
    },
  ], []);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'GROUPED', icon: <IconSystem.grid size={16} />, label: 'Grouped', tooltip: 'One row per customer group' },
    { id: 'DETAILED', icon: <IconSystem.list size={16} />, label: 'Detailed', tooltip: 'One row per customer ledger' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.refresh(load) },
    ...(can('EXPORT')
      ? [{ id: 'export', icon: <IconSystem.download size={16} />, label: 'Export', onClick: handleExport, variant: 'icon' as const }]
      : []),
  ];

  const ccy = data?.baseCurrencyCode || '';
  const appliedCount =
    (applied.entityIds.length ? 1 : 0) + (applied.businessUnitIds.length ? 1 : 0) +
    (applied.groupFilterId ? 1 : 0) + (applied.includeZero ? 1 : 0);

  return (
    <EnterpriseScreenShell
      title="AR Outstanding"
      description="Customer balances as at a date, straight from the accounting records"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.dollarSign size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      viewModeConfig={{
        currentMode: effectiveMode,
        modes: viewModeOptions,
        onChange: (m) => { setDrill(null); setViewMode(m as ViewMode); setPage(1); },
      }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  As On Date <span className="text-danger">*</span>
                </label>
                <input type="date" className="form-input" value={asOnDate}
                  onChange={e => setAsOnDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              {entityOptions.length > 1 && (
                <div className="filter-section">
                  <SyutSelect label="Entities" isMulti options={entityOptions}
                    value={selectedEntityIds}
                    onChange={v => setSelectedEntityIds((v as string[]) || [])}
                    placeholder="All my entities" size="sm" menuPortalTarget={document.body} />
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Consolidates across the selected entities. Empty = all your entities.
                  </div>
                </div>
              )}
              <div className="filter-section">
                <SyutSelect label="Business Units" isMulti options={buOptions}
                  value={selectedBuIds}
                  onChange={v => setSelectedBuIds((v as string[]) || [])}
                  placeholder="All business units" size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Customer Group" options={groupOptions}
                  value={groupFilterId}
                  onChange={v => setGroupFilterId((v as string) || '')}
                  isClearable={false} size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeZero}
                    onChange={e => setIncludeZero(e.target.checked)} /> Include zero balances
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setSelectedEntityIds([]); setSelectedBuIds([]); setGroupFilterId('');
                setIncludeZero(false); setAsOnDate(today());
              }}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'As On', value: applied.asOnDate },
          { label: 'View', value: effectiveMode === 'GROUPED' ? 'Grouped' : 'Detailed' },
          { label: 'Entities', value: applied.entityIds.length ? `${applied.entityIds.length} selected` : 'All' },
          { label: 'Business Units', value: applied.businessUnitIds.length ? `${applied.businessUnitIds.length} selected` : 'All' },
          { label: 'Customers', value: data?.customerCount ?? 0 },
          {
            label: data?.mixedBaseCurrency ? 'Total' : `Total ${ccy}`,
            value: data?.mixedBaseCurrency ? 'Mixed currencies' : fmtMoney(data?.grandTotal),
          },
        ]}
      />

      {isStale && (
        <div style={{
          margin: '0.5rem 1rem', padding: '0.4rem 0.75rem', borderRadius: '0.5rem',
          background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
          fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <IconSystem.info size={14} />
          <span>Filters changed — click <strong>Apply</strong> to refresh the report.</span>
        </div>
      )}

      {data?.mixedBaseCurrency && (
        <div style={{
          margin: '0.5rem 1rem', padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
          background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)',
          fontSize: '0.8125rem',
        }}>
          The selected entities do not share one functional currency, so a combined total is not
          shown. Filter to entities with the same base currency to see a grand total.
        </div>
      )}

      {drill && (
        <div style={{
          margin: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem',
        }}>
          <button className="btn btn-secondary" onClick={clearDrill} style={{ padding: '0.25rem 0.6rem' }}>
            <IconSystem.chevronLeft size={14} /> All groups
          </button>
          <span style={{ color: 'var(--text-muted)' }}>Showing customers in</span>
          <strong>{drill.name}</strong>
        </div>
      )}

      <div className="data-grid-container">
        <SyutGrid
          data={gridRows}
          columns={effectiveMode === 'GROUPED' ? groupedColumns : detailedColumns}
          enablePagination={effectiveMode === 'DETAILED'}
          manualPagination
          totalCount={data?.totalCount ?? 0}
          currentPage={page - 1}
          pageSize={pageSize}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          emptyMessage={loading ? 'Loading...' : 'No outstanding balances for the selected filters.'}
        />
      </div>

      {statementLedger && (
        <LedgerStatementDrillModal
          row={statementLedger}
          asOnDate={asOnDate}
          businessUnitIds={applied.businessUnitIds}
          onClose={() => setStatementLedger(null)}
        />
      )}
    </EnterpriseScreenShell>
  );
};

// ---------------------------------------------------------------------------
// DRILL LEVEL 3 — Ledger Statement for one customer ledger, shown in-place.
// ---------------------------------------------------------------------------

const LedgerStatementDrillModal: React.FC<{
  row: ArOutstandingRowDto;
  asOnDate: string;
  businessUnitIds: string[];
  onClose: () => void;
}> = ({ row, asOnDate, businessUnitIds, onClose }) => {
  const [stmt, setStmt] = useState<LedgerStatementDto | null>(null);
  const [loading, setLoading] = useState(true);
  // Statement window: the financial-year-ish span ending at the report date. A wide
  // opening window keeps the closing balance consistent with the report figure.
  const fromDate = useMemo(() => {
    const d = new Date(asOnDate);
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  }, [asOnDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await reportService.getArOutstandingLedger(row.ledgerId!, fromDate, asOnDate, businessUnitIds);
        if (!cancelled) setStmt(s);
      } catch (err: any) {
        if (!cancelled) {
          notificationService.toast.error(err?.response?.data?.message || 'Could not load the ledger statement.');
          setStmt(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.ledgerId, fromDate, asOnDate, businessUnitIds]);

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div>
            <h2 className="modal-title">{row.partyName || row.ledgerName}</h2>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Ledger statement · {fromDate} to {asOnDate}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          {loading && <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Loading statement...</div>}
          {!loading && stmt && (
            <>
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', fontSize: '0.8125rem' }}>
                <div><span style={{ color: 'var(--text-muted)' }}>Opening: </span><strong>{fmtMoney(stmt.openingBalance)}</strong></div>
                <div><span style={{ color: 'var(--text-muted)' }}>Closing: </span><strong>{fmtMoney(stmt.closingBalance)}</strong></div>
                <div><span style={{ color: 'var(--text-muted)' }}>Report outstanding: </span><strong>{fmtMoney(row.outstanding)}</strong></div>
              </div>
              <div style={{ maxHeight: '55vh', overflow: 'auto' }}>
                <table className="syut-grid" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Date</th><th>Voucher</th><th>Type</th><th>Narration</th>
                      <th style={{ textAlign: 'right' }}>Debit</th>
                      <th style={{ textAlign: 'right' }}>Credit</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stmt.lines ?? []).map((l, i) => (
                      <tr key={i}>
                        <td>{(l.transactionDate || '').slice(0, 10)}</td>
                        <td><DrillableCell title="Transaction details">{l.transactionNo}</DrillableCell></td>
                        <td>{l.voucherTypeName}</td>
                        <td>{l.narration || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{l.drAmount ? fmtMoney(l.drAmount) : ''}</td>
                        <td style={{ textAlign: 'right' }}>{l.crAmount ? fmtMoney(l.crAmount) : ''}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoney(l.runningBalance)}</td>
                      </tr>
                    ))}
                    {(stmt.lines ?? []).length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
                        No movements in this period.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default ArOutstandingScreen;
